import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { API } from "../config/api";
import { useAIAdvice } from "../useAIAdvice";
import PlotSummary from "./PlotSummary";

const SUMMARY_META = {
  // cycle-based (GAPL / PAGL)
  total_gas_injected_mcf:     { label: "Gas Injected",            unit: "MCF" },
  total_liquid_recovery_bbl:  { label: "Liquid Recovery",         unit: "bbl" },
  avg_injection_rate_mcfd:    { label: "Avg Injection Rate",      unit: "MCFD" },
  expected_arrivals:          { label: "Expected Arrivals",       unit: "cycles" },
  // plunger
  expected_plunger_arrivals:  { label: "Expected Plunger Arrivals", unit: "cycles" },
  avg_arrival_velocity:       { label: "Avg Arrival Velocity",    unit: "ft/min" },
  total_cycle_volume_mcf:     { label: "Total Cycle Volume",      unit: "MCF" },
  // gas lift 24 h
  liquid_recovery_24h_bbl:    { label: "Liquid Recovery (24 h)",  unit: "bbl" },
  gas_production_24h_mcf:     { label: "Gas Production (24 h)",   unit: "MCF" },
  gas_injected_24h_mcf:       { label: "Gas Injected (24 h)",     unit: "MCF" },
  hours_above_critical:       { label: "Hours Above Critical",    unit: "h" },
  forecast_glr_scfbbl:        { label: "Forecast GLR",            unit: "scf/bbl" },
};

function fmt(v, unit) {
  if (v == null || Number.isNaN(v)) return "—";
  const a = Math.abs(v);
  const d = a >= 1000 ? 0 : a >= 100 ? 1 : a >= 10 ? 1 : 2;
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
}

function trendOf(kpi) {
  const f = kpi.forecast || [];
  if (f.length < 2 || f[0].y == null || f[f.length - 1].y == null) return { arrow: "→", pct: null };
  const base = kpi.history?.length ? kpi.history[kpi.history.length - 1].y : f[0].y;
  const end = f[f.length - 1].y;
  if (base == null || Math.abs(base) < 1e-9) return { arrow: "→", pct: null };
  const pct = ((end - base) / Math.abs(base)) * 100;
  return { arrow: pct > 3 ? "↑" : pct < -3 ? "↓" : "→", pct };
}

function Tip({ active, payload, label, C, MONO, unit, isHours, kpi, kpis }) {
  const { advice, loading, fetchAdvice } = useAIAdvice();
  const [askedKey, setAskedKey] = useState(null);

  const row = active && payload?.length ? (payload[0].payload || {}) : {};
  const x = row.x;
  const isFc = !!(active && payload?.length && row.fc != null && x > (row.lastX ?? -Infinity));
  const stepIdx = isFc && kpi ? (kpi.forecast || []).findIndex(f => f.x === x) : -1;
  const adviceKey = isFc && kpi ? `${kpi.key}_${x}_${row.fc}` : null;

  useEffect(() => {
    if (!isFc || stepIdx < 0 || !kpi) return;
    const timer = setTimeout(() => {
      const r2 = v => Math.round(v * 100) / 100;
      const lastAct = kpi.history?.length ? kpi.history[kpi.history.length - 1].y : null;
      const fields = [
        { field: "forecast_step", label: isHours ? "Hours ahead" : "Cycles ahead", value: stepIdx + 1, unit: isHours ? "h" : "cycles" },
        ...(lastAct != null ? [{ field: "last_actual", label: `Last actual ${kpi.label}`, value: r2(lastAct), unit }] : []),
        ...(row.low != null && row.high != null ? [
          { field: "fc_low",  label: `${kpi.label} range low (~80%)`,  value: r2(row.low),  unit },
          { field: "fc_high", label: `${kpi.label} range high (~80%)`, value: r2(row.high), unit },
        ] : []),
        ...(kpis || []).map(k => {
          const pt = k.forecast?.[stepIdx];
          return pt?.y != null ? { field: k.key, label: `Forecast ${k.label}`, value: r2(pt.y), unit: k.unit } : null;
        }).filter(Boolean),
      ];
      const context = Object.fromEntries(fields.map(f => [f.field, f.value]));
      setAskedKey(adviceKey);
      fetchAdvice(`fc_${kpi.key}`, row.fc, unit, isHours ? null : x, context, fields);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFc, adviceKey]);

  if (!active || !payload?.length) return null;
  const head = isHours ? (x > 0 ? `+${x} h (forecast)` : x === 0 ? "now" : `${x} h`) : `Cycle ${x}${row.fc != null ? " (forecast)" : ""}`;
  const ac = !advice ? C.muted
    : advice.startsWith("■") || advice.startsWith("▽") ? C.red
    : advice.startsWith("▲") || advice.startsWith("▶") ? C.amber
    : C.green;
  const adviceText = advice ? advice.replace(/^[◆▲▶■▽]\s*/, "") : "";
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border2}`, borderRadius: 8, padding: "8px 12px", fontFamily: MONO, fontSize: 11 }}>
      <div style={{ color: C.muted, marginBottom: 4 }}>{head}</div>
      {row.hist != null && <div style={{ color: C.cyan }}>Actual: {fmt(row.hist)} {unit}</div>}
      {row.fc != null && x > (row.lastX ?? -Infinity) && (
        <>
          <div style={{ color: C.purple }}>AI forecast: {fmt(row.fc)} {unit}</div>
          {row.low != null && <div style={{ color: C.muted }}>Range: {fmt(row.low)} – {fmt(row.high)} {unit}</div>}
        </>
      )}
      {isFc && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`, maxWidth: 300, fontSize: 10, lineHeight: 1.6, borderLeft: `3px solid ${ac}`, paddingLeft: 8 }}>
          {(askedKey !== adviceKey || loading) ? <span style={{ color: C.muted }}>Getting AI recommendation...</span>
            : advice ? <span style={{ color: ac }}>{adviceText}</span>
            : <span style={{ color: C.muted }}>Hover a forecast point for AI advice</span>}
        </div>
      )}
    </div>
  );
}

const NO_HIDE = [];

export default function ForecastPanel({ endpoint, params, C, MONO, SANS, title, accentColor, refreshKey, hide = NO_HIDE, plotSummary = false }) {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wellIdx, setWellIdx] = useState(0);
  const [kpiKey, setKpiKey] = useState(null);

  const paramsKey = JSON.stringify(params || {});
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    axios.get(`${API}/${endpoint}`, { params: params || {} })
      .then(r => { if (!cancelled) { setRaw(r.data); setWellIdx(0); } })
      .catch(e => { if (!cancelled) { setRaw(null); setError(e.response?.data?.error || e.message); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramsKey, refreshKey]);

  const wells = raw?.wells || null;
  const payload = wells ? wells[Math.min(wellIdx, wells.length - 1)] : raw;
  const kpis = useMemo(() => (payload?.kpis || []).filter(k => !hide.includes(k.key)), [payload, hide]);
  const isHours = payload?.horizon?.type === "hours";
  const steps = payload?.horizon?.steps;
  const ac = accentColor || C.purple;

  useEffect(() => {
    if (kpis.length && !kpis.find(k => k.key === kpiKey)) setKpiKey(kpis[0].key);
  }, [kpis, kpiKey]);

  const kpi = kpis.find(k => k.key === kpiKey) || kpis[0];

  const chart = useMemo(() => {
    if (!kpi) return { data: [], lastX: 0 };
    const hist = (kpi.history || []).filter(h => h.y != null);
    const lastX = hist.length ? hist[hist.length - 1].x : (payload?.last_actual_x ?? 0);
    const rows = hist.map(h => ({ x: h.x, hist: h.y, lastX }));
    const fc = kpi.forecast || [];
    if (hist.length && fc.length) {
      // anchor the dashed forecast line to the last actual point
      rows[rows.length - 1].fc = hist[hist.length - 1].y;
    }
    fc.forEach(f => {
      const hasBand = f.low != null && f.high != null;
      rows.push({
        x: f.x, fc: f.y, low: f.low, high: f.high, lastX,
        bandBase: hasBand ? f.low : undefined,
        bandSpread: hasBand ? Math.max(f.high - f.low, 0) : undefined,
      });
    });
    return { data: rows, lastX };
  }, [kpi, payload]);

  const card = {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 16px",
    position: "relative", marginBottom: 18,
  };
  const topBar = (
    <div style={{ position: "absolute", top: 0, left: 20, right: 20, height: 2, background: `linear-gradient(to right, transparent, ${ac}88, transparent)` }} />
  );
  const heading = title || (isHours ? `AI Forecast: Next ${steps || 24} Hours` : `AI Forecast: Next ${steps || 5} Cycles`);

  if (loading) {
    return (
      <div style={card}>{topBar}
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>{heading}</div>
        <div style={{ fontFamily: MONO, fontSize: 11, color: C.muted, marginTop: 10 }}>Running AI forecast…</div>
      </div>
    );
  }
  if (error || !payload || !kpis.length) {
    return (
      <div style={card}>{topBar}
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>{heading}</div>
        <div style={{ fontFamily: MONO, fontSize: 11, color: C.red, marginTop: 10 }}>{error || "No forecast available for this well."}</div>
      </div>
    );
  }

  const summary = Object.entries(payload.summary || {}).filter(([k, v]) => v != null && SUMMARY_META[k] && !hide.includes(k));
  const isProb = kpi?.kind === "probability";
  const unit = kpi?.unit || "";
  const bt = kpi?.backtest;
  const xTick = v => (isHours ? (v > 0 ? `+${v}h` : v === 0 ? "now" : `${v}h`) : `${v}`);
  const groups = [...new Set(kpis.map(k => k.group))];
  const times = payload.x_times || [];

  const stepLabel = i => {
    const t = times[i];
    if (isHours) {
      return t ? new Date(t).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : `+${i + 1} h`;
    }
    return t ? new Date(t).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  };

  const chartEl = (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chart.data} margin={{ top: 8, right: 14, left: -4, bottom: 30 }}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="x" type="number" domain={["dataMin", "dataMax"]} tickFormatter={xTick}
              tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }} tickLine={false}
              label={{ value: isHours ? "Hours (0 = now)" : "Cycle", position: "insideBottom", offset: -16, fill: C.muted, fontSize: 9, fontFamily: MONO }}
            />
            <YAxis
              domain={isProb ? [0, 100] : ["auto", "auto"]}
              tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }} tickLine={false} axisLine={false} width={56}
              tickFormatter={v => fmt(v)}
              label={{ value: `${kpi.label} (${unit})`, angle: -90, position: "insideLeft", offset: 12, fill: C.muted, fontSize: 9, fontFamily: MONO, style: { textAnchor: "middle" } }}
            />
            <Tooltip isAnimationActive={false} wrapperStyle={{ zIndex: 99999 }} content={<Tip C={C} MONO={MONO} unit={unit} isHours={isHours} kpi={kpi} kpis={kpis} />} />
            <Area dataKey="bandBase" stackId="band" stroke="none" fill="transparent" isAnimationActive={false} legendType="none" activeDot={false} />
            <Area dataKey="bandSpread" stackId="band" stroke="none" fill={ac} fillOpacity={0.18} isAnimationActive={false} legendType="none" activeDot={false} />
            <ReferenceLine x={chart.lastX} stroke={C.muted} strokeDasharray="4 3" label={{ value: "now", fill: C.muted, fontSize: 9, fontFamily: MONO, position: "top" }} />
            <Line dataKey="hist" name="Actual" stroke={C.cyan} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line dataKey="fc" name="AI forecast" stroke={ac} strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: isHours ? 0 : 4, fill: ac }} activeDot={{ r: 5 }} isAnimationActive={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
  );

  return (
    <div style={card}>
      {topBar}

      {/* header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 14, color: C.text }}>{heading}</div>
            <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: 1, color: ac, background: `${ac}18`, border: `1px solid ${ac}55`, borderRadius: 3, padding: "2px 7px" }}>AI PREDICTION</span>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 3 }}>
            {payload.well}
          </div>
        </div>
        {wells && wells.length > 1 && (
          <select
            value={wellIdx}
            onChange={e => setWellIdx(Number(e.target.value))}
            style={{ background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 8, padding: "8px 12px", fontFamily: MONO, fontSize: 11, cursor: "pointer" }}
          >
            {wells.map((w, i) => <option key={i} value={i}>{w.well}</option>)}
          </select>
        )}
      </div>

      {/* summary tiles */}
      {summary.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 16 }}>
          {summary.map(([k, v]) => (
            <div key={k} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{SUMMARY_META[k].label}</div>
              <div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: ac, marginTop: 4 }}>
                {fmt(v)}<span style={{ fontSize: 10, color: C.muted, marginLeft: 4 }}>{SUMMARY_META[k].unit}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* KPI selector */}
      {groups.map(g => (
        <div key={g} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted2 || C.muted, textTransform: "uppercase", letterSpacing: 1, minWidth: 78 }}>{g}</span>
          {kpis.filter(k => k.group === g).map(k => {
            const t = trendOf(k);
            const sel = k.key === kpi.key;
            const next = k.forecast?.[0]?.y;
            return (
              <button
                key={k.key}
                onClick={() => setKpiKey(k.key)}
                style={{
                  cursor: "pointer", fontFamily: MONO, fontSize: 10, borderRadius: 8, padding: "6px 10px",
                  color: sel ? C.text : C.muted, background: sel ? `${ac}22` : C.panel,
                  border: `1px solid ${sel ? ac : C.border2}`,
                }}
              >
                {k.label}
                <span style={{ marginLeft: 8, color: sel ? ac : C.muted }}>{fmt(next)}{k.kind === "probability" ? "%" : ""}</span>
                <span style={{ marginLeft: 6, color: t.arrow === "↑" ? C.green : t.arrow === "↓" ? C.amber : C.muted }}>{t.arrow}</span>
              </button>
            );
          })}
        </div>
      ))}

      {/* chart */}
      <div style={{ height: 290, marginTop: 14 }}>
        {chartEl}
      </div>

      {/* legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 4 }}>
        <span><span style={{ color: C.cyan }}>━</span> Actual</span>
        <span><span style={{ color: ac }}>╍╍</span> AI forecast</span>
        {!isProb && <span><span style={{ color: ac, opacity: 0.5 }}>▮</span> ~80% range</span>}
      </div>

      {plotSummary && (
        <PlotSummary
          inline
          lensId="forecast"
          title={`${heading}: ${kpi.label} (${unit})`}
          subtitle={`${payload.well || ""} AI forecast of ${kpi.label} for the next ${isHours ? "hours" : "cycles"}. Solid line = actual history, dashed line after "now" = AI forecast${isProb ? "" : " with ~80% range"}.`}
          accent={ac}
        >
          {chartEl}
        </PlotSummary>
      )}

      {/* step table */}
      <div style={{ overflowX: "auto", marginTop: 16, border: `1px solid ${C.border}`, borderRadius: 10, maxHeight: isHours ? 360 : "none", overflowY: isHours ? "auto" : "visible" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 11 }}>
          <thead>
            <tr style={{ background: C.panel, position: "sticky", top: 0 }}>
              <th style={{ textAlign: "left", padding: "8px 10px", color: C.muted, fontWeight: 400, whiteSpace: "nowrap" }}>{isHours ? "Hour" : "Cycle"}</th>
              {kpis.map(k => (
                <th key={k.key} style={{ textAlign: "right", padding: "8px 10px", color: k.key === kpi.key ? ac : C.muted, fontWeight: 400, whiteSpace: "nowrap" }}>
                  {k.label} <span style={{ opacity: 0.7 }}>({k.unit})</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(kpi.forecast || []).map((f, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={{ padding: "7px 10px", color: C.text, whiteSpace: "nowrap" }}>
                  {isHours ? `+${i + 1} h` : `#${f.x}`}
                  <span style={{ color: C.muted, marginLeft: 8, fontSize: 9 }}>{stepLabel(i)}</span>
                </td>
                {kpis.map(k => {
                  const p = k.forecast?.[i];
                  return (
                    <td key={k.key} title={p?.low != null ? `${fmt(p.low)} – ${fmt(p.high)} ${k.unit}` : undefined}
                        style={{ textAlign: "right", padding: "7px 10px", color: k.key === kpi.key ? ac : C.text, whiteSpace: "nowrap" }}>
                      {fmt(p?.y)}{k.kind === "probability" && p?.y != null ? "%" : ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}